<script lang="ts" setup>
import '../../../css/choice.css';
import { reactive, ref, computed, onBeforeMount } from 'vue';
import TextInput from "../../components/TextInput.vue";
import { ChoiceApi, SectionPanel, DropDownSplitButton, DropDownButtonItem, TitledRow, LoadingSection } from '../../LibMain';
import { ReasonCategoryOptions, INewEnhancedAccessRequest } from '../../lib/Api/EnhancedAccess';
import { ValidationResults } from '../../lib/Models/ValidationResults';
import ChoiceStateMessageSink from '../../components/ChoiceStateMessageSink.vue';

const ReasonBody = ref();
const compData = reactive<{
  reasonCategory?: ReasonCategoryOptions,
  ticketNumber: string,
  reasonExplanation: string,
  showReason?: boolean
}>({  reasonCategory: undefined,
      ticketNumber : "",
      reasonExplanation: "",
    showReason : true});

const currentReasonCategory = computed<string>(() =>
  compData.reasonCategory ?? "Select Reason"
);
const props = withDefaults(defineProps<{
  hasEnhancedAccess: boolean
}>(), {

});
onBeforeMount(async () => {
  if (props.hasEnhancedAccess) {
    compData.showReason = false;
    return;
  }
})
const invalidSubmit = computed<boolean>(() => {
  if (!(OtherAndNoReason())) {
    return false;
  }
  if (!compData.reasonCategory) {
    return false;
  }
  return true;
});

function updateReasonCategory(opt: ReasonCategoryOptions) {
  compData.reasonCategory = opt;
  ReasonBody.value?.performValidation();
}

async function onSubmit() {
  compData.showReason = undefined;
  const request = <INewEnhancedAccessRequest>({
    ReasonCategory: compData.reasonCategory,
    TicketNumber: compData.ticketNumber,
    ReasonBody: compData.reasonExplanation
  });
  const result = await ChoiceApi.enhancedAccess.requestAccess(request);
  if (!result) {
    compData.showReason = true;
    return;
  }
  if (result?.successful) {
    compData.showReason = false;
    MessageLib.MainWindow.refreshPrivileges();
    return;
  }
}

function OtherAndNoReason(): boolean {
  if (!compData.reasonExplanation && compData.reasonCategory === ReasonCategoryOptions.Other) {
    return false;
  }
  return true;
}

function RequiredIfOtherSelected(value: string): ValidationResults {
  if ((OtherAndNoReason())) {
    return { isValid: true };
  }
  return { isValid: false, errorMsg: "Must have explanation if \"Other\" reason selected" };

}
async function exitEnhancedAccess(){
  compData.showReason = undefined;
  const result = await ChoiceApi.enhancedAccess.removeAccess();
  if(!result){
    compData.showReason = false;
    return;
  }
  if (result?.successful) {
    compData.showReason = true;
    MessageLib.MainWindow.refreshPrivileges();
    resetData();
    return;
  }
}
function resetData(){
  const compDefaults = {
    reasonCategory: undefined,
    ticketNumber : "",
    reasonExplanation: "",
    showReason : true
  };
  Object.assign(compData, compDefaults);
}
</script>
<template>
  <div v-if="true === compData.showReason">
    <SectionPanel :title="'Enhanced Access Reason'" class="mb-2">
      <div class="mb-2">
        Please enter a reason for Enhanced Access.
      </div>
      <div class="mb-2">
        Be sure to include SFDC Case or DevOps #
      </div>
      <TitledRow title="Reason" :titleWidth="1" class="mb-2">
        <DropDownSplitButton>
          <template v-slot:button>{{ currentReasonCategory }}</template>
          <DropDownButtonItem v-for="ReasonOption in ReasonCategoryOptions" :data="ReasonOption" :width="{ amount: 70 }"
            @click="updateReasonCategory">
            {{ ReasonOption }}
          </DropDownButtonItem>
        </DropDownSplitButton>
      </TitledRow>
      <TitledRow title="Identifier" :titleWidth="1" class="mb-2">
        <TextInput v-model:textValue="compData.ticketNumber" placeholder="Ticket Number" :maxLength="50" />
      </TitledRow>
      <TextInput ref="ReasonBody" class="mb-2" v-model:textValue="compData.reasonExplanation" :rows="5"
        :maxLength="1200" :validator="RequiredIfOtherSelected" />
      <div class="mb-2">
        Enhanced Access will be active as long as you are logged in to the customer database.
      </div>
      <div class="mb-2">
        If you are logged out due to inactivity, you will need to reenter Enhanced Access.
      </div>
      <button class="btn btn-primary btn-key" @click="onSubmit()" :disabled="!invalidSubmit">
        Submit
      </button>
    </SectionPanel>
  </div>
  <div v-if="false === compData.showReason">
    <div class="d-flex flex-column justify-content-center align-items-center" style="height: 100vh;">
      <div class="alert alert-success text-center p-5 w-50">
        <h1 class="display-4">Enhanced Access Mode Enabled</h1>
      </div>
    </div>
    <button class="btn btn-primary btn-danger btn-key position-fixed" style="bottom: 20px; left: 20px;" @click="exitEnhancedAccess()">Exit Enhanced Access</button>
  </div>
  <div v-if="undefined === compData.showReason">
    <LoadingSection :isLoading="true" loadingMsg="Checking Enhanced Access Status">

    </LoadingSection>
  </div>
  <ChoiceStateMessageSink />
</template>